import { MAX_LIST_LEVEL, type ProseBlock } from '@libs/types';

/**
 * Ordinal number for every `list: 'number'` block in a section, keyed by
 * `blockId`.
 *
 * The delivered document groups consecutive `list_item` blocks into one
 * `<ol>`/`<ul>` and lets the browser number the `<ol>` natively, so for this
 * pane to look the same an item's ordinal has to be its position within that
 * same run — the current unbroken stretch of `list: 'number'` items at its own
 * `level` — never the block's index in `section.blocks`.
 *
 * A separate counter is kept per level so a nested sub-list never advances its
 * parent's count: "1. Step / a. sub / b. sub / 2. Step" numbers the two steps
 * 1 and 2 no matter how many sub-items sit between them. Descending into a
 * level for the first time under a new parent always starts that level over
 * at 1 — the same way a markdown renderer restarts a nested ordered list under
 * each new parent item, rather than carrying on the previous parent's nested
 * count. A bullet item only breaks the run at its own level (and clears
 * whatever was nested under it) since it says nothing about a numbered list
 * further up the tree; anything that is not a list item at all — a heading,
 * paragraph, table or diagram — ends every open run, because the document has
 * visibly moved on to something else.
 */
export function computeListOrdinals(blocks: ProseBlock[]): Map<string, number> {
  const ordinals = new Map<string, number>();
  const counts = new Array<number>(MAX_LIST_LEVEL + 1).fill(0);
  let prevDepth: number | null = null;

  for (const block of blocks) {
    if (block.kind !== 'list_item') {
      counts.fill(0);
      prevDepth = null;
      continue;
    }

    const depth = Math.min(Math.max(block.level ?? 0, 0), MAX_LIST_LEVEL);

    if ((block.list ?? 'bullet') !== 'number') {
      counts[depth] = 0;
      for (let d = depth + 1; d < counts.length; d++) counts[d] = 0;
      prevDepth = depth;
      continue;
    }

    counts[depth] = prevDepth === null || depth > prevDepth ? 1 : counts[depth] + 1;
    // A level entered less deeply than before invalidates whatever a deeper
    // level had been counting — the next descent is under a new parent, so
    // that nested run has to start fresh rather than pick up a stale count.
    for (let d = depth + 1; d < counts.length; d++) counts[d] = 0;

    ordinals.set(block.blockId, counts[depth]);
    prevDepth = depth;
  }

  return ordinals;
}
